import React, { useMemo, useState } from 'react';

import {
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MoreHoriz as MoreHorizIcon } from '@material-ui/icons';
import clsx from 'clsx';
import type { FormikProps } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import processing_activities_messages from 'messages/processing_activities_messages';
import PATHS from 'router/PATHS';

import { isLegitimateInterestsBasis, formatEditorPurposeRetentionPeriod } from '../processingActivityEditor.parsers';
import type { ProcessingActivityEditorFormValues, ProcessingActivityEditorPurpose } from '../processingActivityEditor.types';
import {
  buildProcessingActivityEditorValidationMessages,
  getPurposeRetentionErrorKey,
  validatePurposeForDialog,
  type ProcessingActivityEditorValidationErrors,
} from '../processingActivityEditor.validation';

import ProcessingActivityPurposeDialog from './ProcessingActivityPurposeDialog';

type ProcessingActivityPurposesSectionProps = {
  formik: FormikProps<ProcessingActivityEditorFormValues>;
  processingActivityId?: number;
  publishValidationErrors?: ProcessingActivityEditorValidationErrors;
};

const useStyles = makeStyles(theme => ({
  sectionHeadingRow: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  tableCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  cellNote: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(0.5),
  },
  liaRequired: {
    color: theme.palette.warning.dark,
    fontWeight: 600,
  },
  liaNotRequired: {
    color: theme.palette.text.secondary,
  },
  menuCell: {
    textAlign: 'right',
    width: 48,
  },
  invalidRow: {
    backgroundColor: theme.palette.error.light,
  },
  errorAction: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    textAlign: 'left',
    textDecoration: 'underline',
  },
  softDeletedRow: {
    backgroundColor: theme.palette.grey[100],
  },
  softDeletedText: {
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
  },
}));

const reassignPurposeSortOrders = (purposes: ProcessingActivityEditorPurpose[]): ProcessingActivityEditorPurpose[] =>
  purposes.map((purpose, index) => ({
    ...purpose,
    sortOrder: index,
  }));

const ProcessingActivityPurposesSection: React.FC<ProcessingActivityPurposesSectionProps> = ({
  formik,
  processingActivityId,
  publishValidationErrors = {},
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const validationMessages = useMemo(() => buildProcessingActivityEditorValidationMessages(t), [t]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activePurposeClientId, setActivePurposeClientId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPurpose, setEditingPurpose] = useState<ProcessingActivityEditorPurpose | null>(null);

  const allPurposes = formik.values.purposes;

  const openCreateDialog = () => {
    setEditingPurpose(null);
    setDialogOpen(true);
  };

  const openEditDialog = (purpose: ProcessingActivityEditorPurpose) => {
    setEditingPurpose(purpose);
    setDialogOpen(true);
    setMenuAnchor(null);
    setActivePurposeClientId(null);
  };

  const handleSavePurpose = (purpose: ProcessingActivityEditorPurpose) => {
    const errors = validatePurposeForDialog(purpose, validationMessages);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const existingIndex = formik.values.purposes.findIndex(item => item.clientId === purpose.clientId);
    if (existingIndex >= 0) {
      const nextPurposes = [...formik.values.purposes];
      nextPurposes[existingIndex] = purpose;
      formik.setFieldValue('purposes', nextPurposes);
      return;
    }

    formik.setFieldValue('purposes', [...formik.values.purposes, { ...purpose, sortOrder: formik.values.purposes.length }]);
  };

  const handleDeletePurpose = (clientId: string) => {
    formik.setFieldValue(
      'purposes',
      formik.values.purposes.map(purpose =>
        purpose.clientId === clientId
          ? {
              ...purpose,
              isSoftDeleted: true,
            }
          : purpose,
      ),
    );
    setMenuAnchor(null);
    setActivePurposeClientId(null);
  };

  const handleRestorePurpose = (clientId: string) => {
    formik.setFieldValue(
      'purposes',
      formik.values.purposes.map(purpose =>
        purpose.clientId === clientId
          ? {
              ...purpose,
              isSoftDeleted: false,
            }
          : purpose,
      ),
    );
    setMenuAnchor(null);
    setActivePurposeClientId(null);
  };

  const handleMovePurpose = (clientId: string, direction: 'up' | 'down') => {
    const currentIndex = formik.values.purposes.findIndex(purpose => purpose.clientId === clientId);
    if (currentIndex < 0) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= formik.values.purposes.length) {
      return;
    }

    const nextPurposes = [...formik.values.purposes];
    [nextPurposes[currentIndex], nextPurposes[targetIndex]] = [nextPurposes[targetIndex], nextPurposes[currentIndex]];
    formik.setFieldValue('purposes', reassignPurposeSortOrders(nextPurposes));
    setMenuAnchor(null);
    setActivePurposeClientId(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, clientId: string) => {
    setMenuAnchor(event.currentTarget);
    setActivePurposeClientId(clientId);
  };

  const handleOpenLia = (purpose: ProcessingActivityEditorPurpose) => {
    setMenuAnchor(null);
    setActivePurposeClientId(null);

    if (!purpose.id) {
      enqueueSnackbar(t(processing_activities_messages.purpose_lia.validation.purpose_not_saved), { variant: 'warning' });
      return;
    }

    const returnPath =
      processingActivityId != null
        ? generatePath(PATHS.PROCESSING_ACTIVITIES_EDIT, { id: String(processingActivityId) })
        : PATHS.PROCESSING_ACTIVITIES;

    history.push({
      pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_PURPOSE_LIA, { purposeId: String(purpose.id) }),
      state: { returnPath },
    });
  };

  const activePurpose = activePurposeClientId
    ? formik.values.purposes.find(purpose => purpose.clientId === activePurposeClientId)
    : undefined;
  const activePurposeIndex = activePurpose ? formik.values.purposes.findIndex(purpose => purpose.clientId === activePurpose.clientId) : -1;

  return (
    <>
      <div className={styles.sectionHeadingRow}>
        <div>
          <Typography className={styles.cellNote} variant='body2'>
            {t(processing_activities_messages.editor.sections.purposes_description)}
          </Typography>
        </div>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={openCreateDialog}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(processing_activities_messages.editor.actions.create_purpose)}
        </ColoredButton>
      </div>
      {publishValidationErrors.purposes && <FormHelperText error>{publishValidationErrors.purposes}</FormHelperText>}

      {allPurposes.length > 0 && (
        <div className={styles.tableCard}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t(processing_activities_messages.editor.table.purpose)}</TableCell>
                <TableCell>{t(processing_activities_messages.editor.table.legal_basis)}</TableCell>
                <TableCell>{t(processing_activities_messages.editor.table.retention_period)}</TableCell>
                <TableCell>{t(processing_activities_messages.editor.table.lia)}</TableCell>
                <TableCell align='right'>{t(processing_activities_messages.editor.table.actions)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allPurposes.map(purpose => {
                const softDeletedClass = purpose.isSoftDeleted ? styles.softDeletedText : undefined;
                const retentionError = purpose.isSoftDeleted
                  ? undefined
                  : publishValidationErrors[getPurposeRetentionErrorKey(purpose.clientId)];

                return (
                  <TableRow
                    key={purpose.clientId}
                    className={clsx(purpose.isSoftDeleted && styles.softDeletedRow, retentionError && styles.invalidRow)}
                  >
                    <TableCell className={softDeletedClass}>
                      <Typography className={softDeletedClass} variant='subtitle2'>
                        {purpose.purposeText}
                      </Typography>
                      {purpose.legalBasisComment && (
                        <div className={clsx(styles.cellNote, softDeletedClass)}>{purpose.legalBasisComment}</div>
                      )}
                    </TableCell>
                    <TableCell className={softDeletedClass}>
                      {purpose.legalBasis ? t(processing_activities_messages.editor.legal_basis_with_article[purpose.legalBasis]) : '—'}
                    </TableCell>
                    <TableCell className={softDeletedClass}>
                      {formatEditorPurposeRetentionPeriod(purpose) || '—'}
                      {retentionError && (
                        <FormHelperText error>
                          <button className={styles.errorAction} onClick={() => openEditDialog(purpose)} type='button'>
                            {retentionError}. {t(processing_activities_messages.editor.actions.edit_purpose)}
                          </button>
                        </FormHelperText>
                      )}
                    </TableCell>
                    <TableCell className={softDeletedClass}>
                      {isLegitimateInterestsBasis(purpose.legalBasis) ? (
                        <span className={purpose.isSoftDeleted ? styles.softDeletedText : styles.liaRequired}>
                          {t(processing_activities_messages.editor.lia_required_chip)}
                        </span>
                      ) : (
                        <span className={purpose.isSoftDeleted ? styles.softDeletedText : styles.liaNotRequired}>
                          {t(processing_activities_messages.editor.lia_not_required)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align='right' className={styles.menuCell}>
                      <IconButton
                        aria-label={t(processing_activities_messages.editor.table.open_actions)}
                        onClick={event => openMenu(event, purpose.clientId)}
                        size='small'
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Typography className={styles.cellNote} variant='caption'>
        {t(processing_activities_messages.editor.soft_delete_note)}
      </Typography>

      <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} open={Boolean(menuAnchor)}>
        {activePurpose && !activePurpose.isSoftDeleted && (
          <MenuItem onClick={() => openEditDialog(activePurpose)}>{t(processing_activities_messages.editor.actions.edit_purpose)}</MenuItem>
        )}
        {activePurpose && !activePurpose.isSoftDeleted && isLegitimateInterestsBasis(activePurpose.legalBasis) && (
          <MenuItem onClick={() => handleOpenLia(activePurpose)}>
            {t(
              activePurpose.id
                ? processing_activities_messages.purpose_lia.actions.open
                : processing_activities_messages.purpose_lia.actions.create,
            )}
          </MenuItem>
        )}
        {activePurpose && activePurpose.isSoftDeleted && activePurposeClientId && (
          <MenuItem onClick={() => handleRestorePurpose(activePurposeClientId)}>
            {t(processing_activities_messages.editor.actions.restore_purpose)}
          </MenuItem>
        )}
        {activePurposeClientId && activePurposeIndex > 0 && (
          <MenuItem onClick={() => handleMovePurpose(activePurposeClientId, 'up')}>
            {t(processing_activities_messages.editor.actions.move_purpose_up)}
          </MenuItem>
        )}
        {activePurposeClientId && activePurposeIndex >= 0 && activePurposeIndex < formik.values.purposes.length - 1 && (
          <MenuItem onClick={() => handleMovePurpose(activePurposeClientId, 'down')}>
            {t(processing_activities_messages.editor.actions.move_purpose_down)}
          </MenuItem>
        )}
        {activePurpose && !activePurpose.isSoftDeleted && activePurposeClientId && (
          <MenuItem onClick={() => handleDeletePurpose(activePurposeClientId)}>
            {t(processing_activities_messages.editor.actions.delete_purpose)}
          </MenuItem>
        )}
      </Menu>

      <ProcessingActivityPurposeDialog
        initialPurpose={editingPurpose}
        onClose={() => setDialogOpen(false)}
        onSave={handleSavePurpose}
        open={dialogOpen}
      />
    </>
  );
};

export default ProcessingActivityPurposesSection;
